import { createContext, useContext, useState, useCallback, useRef } from "react";

const NotificationContext = createContext(null);

const TYPE_CONFIG = {
  "consultation:new": {
    label: "New Consultation",
    color: "#065F46",
    bg: "#D1FAE5",
    accent: "#059669",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
  },
  "contact:new": {
    label: "New Message",
    color: "#1D4ED8",
    bg: "#DBEAFE",
    accent: "#2563EB",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
  },
  "quote:new": {
    label: "New Quote",
    color: "#92400E",
    bg: "#FEF3C7",
    accent: "#D97706",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 11 12 14 22 4"/>
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
      </svg>
    ),
  },
  "service-request:new": {
    label: "New Service Request",
    color: "#6D28D9",
    bg: "#EDE9FE",
    accent: "#7C3AED",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
      </svg>
    ),
  },
};

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [toasts, setToasts] = useState([]);
  const toastId = useRef(0);

  const pushNotification = useCallback((type, payload) => {
    const cfg = TYPE_CONFIG[type];
    if (!cfg) return;

    const id    = `${type}-${Date.now()}-${Math.random()}`;
    const entry = { id, type, cfg, payload, at: new Date() };

    setNotifications(prev => [entry, ...prev].slice(0, 50));

    const tid = ++toastId.current;
    setToasts(prev => [...prev, { ...entry, tid }]);

    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.tid !== tid));
    }, 5000);
  }, []);

  const dismissToast       = useCallback((tid) => setToasts(prev => prev.filter(t => t.tid !== tid)), []);
  const clearNotifications = useCallback(() => setNotifications([]), []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      toasts,
      unreadCount: notifications.length,
      pushNotification,
      dismissToast,
      clearNotifications,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used inside NotificationProvider");
  return ctx;
}

export { TYPE_CONFIG };